/**
 * Eine Stellplatz-Zeile (Board „Belegung · Stellplätze“): dieselben Maße wie die Apartment-Zeile.
 * Die ganze Beschriftung ist die Auswahl; darunter Auslastung oder „Stillgelegt“. Heute ist grün
 * getönt, das Wochenende auf Papier. Bei Dichte 14 öffnet ein Tipp auf die Zeile ihre Übersicht.
 */
import { Check } from 'lucide-react';
import type { ParkingOccupancySpotRow } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { ParkingReservationPill } from './ParkingReservationPill';
import type { RowGeometry } from './CalendarPropertyRow';
import { isWeekend, monthBreakIndices } from './calendarHelpers';
import { buildParkingSpans, occupiedDayCount } from './parkingCalendarHelpers';

interface Props {
  row: ParkingOccupancySpotRow;
  dates: string[];
  today: string;
  geometry: RowGeometry;
  selected: boolean;
  onToggle: () => void;
  /** Dichte 14 am Handy: die Zellen sind zu schmal für Balken mit Text. */
  onOverview?: () => void;
}

export function ParkingSpotRow({ row, dates, today, geometry, selected, ...props }: Props) {
  const breaks = monthBreakIndices(dates);
  const spans = buildParkingSpans(row.reservations, dates);
  const percent =
    dates.length === 0 ? 0 : Math.round((occupiedDayCount(spans) / dates.length) * 100);
  // Ein stillgelegter Platz kann eine laufende Belegung tragen – er verschwindet nicht, er sagt
  // nur, dass nichts Neues mehr darauf geht.
  const sub = row.isActive ? `${percent} % belegt` : 'Stillgelegt';
  const compact = geometry.compact;
  // In der 72-px-Spalte am Handy nur die Kurzform; der volle Satz steht im Namen der Auswahl.
  const shortSub = row.isActive ? `${percent} %` : 'aus';
  return (
    <div
      className={cn('border-card-alt relative grid border-b', selected && 'bg-forest-soft/40')}
      style={{
        gridTemplateColumns: `${geometry.sidebarWidth}px repeat(${dates.length}, minmax(${geometry.cellMin}px, 1fr))`,
        gridTemplateRows: `${geometry.rowHeight}px`,
      }}
    >
      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-label={`${row.label} auswählen, ${sub}`}
        onClick={props.onToggle}
        className={cn(
          'sticky left-0 z-20 flex min-h-11 min-w-0 items-center text-left',
          selected ? 'bg-forest-soft' : 'bg-card',
          compact ? 'gap-1.5 pl-2 pr-1' : 'gap-3 px-[18px]',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'grid size-[22px] shrink-0 place-items-center rounded-[7px]',
            selected ? 'bg-forest text-white' : 'border-ink-faint/50 border-[1.5px] bg-white',
          )}
        >
          {selected && <Check size={14} strokeWidth={3} />}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[13px] font-bold md:text-sm">{row.label}</span>
          <span
            className={cn(
              'truncate text-[10px] md:text-xs',
              row.isActive ? 'text-ink-soft' : 'text-amber-ink font-semibold',
            )}
          >
            {compact ? shortSub : sub}
          </span>
        </span>
      </button>
      {dates.map((date, index) => (
        <div
          key={date}
          style={{ gridColumn: index + 2, gridRow: 1 }}
          className={cn(
            'border-card-alt border-l',
            isWeekend(date) && 'bg-paper',
            date === today && 'bg-forest/[0.06]',
            breaks.has(index) && 'border-l-line',
          )}
        />
      ))}
      {props.onOverview ? (
        <button
          type="button"
          aria-label={`${row.label}: Belegungen und freie Tage`}
          onClick={props.onOverview}
          // Über den Balken: bei 14 Tagen sind sie zu schmal, um einzeln getroffen zu werden.
          className="absolute inset-y-0 right-0 z-[15]"
          style={{ left: geometry.sidebarWidth }}
        />
      ) : null}
      {spans.map((span) => (
        <ParkingReservationPill
          key={span.entry.reservationId}
          span={span}
          gridStart={span.startIndex + 2}
          mobile={compact}
          showLabel={geometry.labels !== 'none'}
        />
      ))}
    </div>
  );
}
